import guideCatalog from '@motexa/user-guide/catalog.json'
import sdkNavigation from '../../../public-sdk/docs/navigation.json'
import type { SiteLocale } from '../types'

const origin = 'https://motexa.top'
const projectUrl = 'https://github.com/shj56166/Motexa'

type SeoPage = { title: string; description: string; type?: 'article' | 'software' }

const staticPages: Record<string, Record<SiteLocale, SeoPage>> = {
  '/': {
    'zh-CN': { title: 'Motexa Ride｜电动两轮车数据与骑行工具', description: '连接兼容的保护板、控制器、车辆与充电器，查看实时状态、骑行仪表、轨迹和能耗记录。' },
    'en-US': { title: 'Motexa Ride | Electric two-wheeler data and rides', description: 'Connect compatible BMS, controllers, vehicles and chargers. View live status, ride dashboards, routes and energy records.' },
  },
  '/download': {
    'zh-CN': { title: '下载 Motexa Ride Android App', description: '下载 Motexa Ride 稳定版或测试版 Android App，查看版本、下载状态和安装校验信息。', type: 'software' },
    'en-US': { title: 'Download Motexa Ride for Android', description: 'Get the stable or beta Motexa Ride Android app and check release and installation details.', type: 'software' },
  },
  '/docs': {
    'zh-CN': { title: 'Motexa Ride 使用教程', description: '从安装、设备连接到骑行记录、投屏与诊断，阅读 Motexa Ride 使用教程。' },
    'en-US': { title: 'Motexa Ride user guides', description: 'Learn installation, device connection, ride recording, casting and diagnostics in Motexa Ride.' },
  },
  '/changelog': {
    'zh-CN': { title: 'Motexa Ride 更新日志', description: '阅读 Motexa Ride 的版本更新内容与功能变化。' },
    'en-US': { title: 'Motexa Ride release notes', description: 'Read Motexa Ride release notes and feature changes.' },
  },
  '/privacy': {
    'zh-CN': { title: 'Motexa Ride 隐私说明', description: '了解 Motexa Ride 的权限用途、骑行数据处理与官网统计方式。' },
    'en-US': { title: 'Motexa Ride privacy', description: 'Learn how Motexa Ride handles permissions, ride data and website analytics.' },
  },
  '/about': {
    'zh-CN': { title: '关于 Motexa Ride', description: '了解 Motexa Ride 电动两轮车工具及其公开项目资料。' },
    'en-US': { title: 'About Motexa Ride', description: 'Learn about Motexa Ride and its public project information.' },
  },
}

function pageFor(path: string, locale: SiteLocale): SeoPage | null {
  const staticPage = staticPages[path]?.[locale]
  if (staticPage) return staticPage
  const guide = path.startsWith('/docs/') ? guideCatalog.articles.find(item => path === `/docs/${item.slug}`) : null
  if (guide) return { title: `${guide.title[locale]}｜Motexa Ride 使用教程`, description: guide.summary[locale], type: 'article' }
  const slug = path === '/docs/developers' ? 'overview' : path.slice('/docs/developers/'.length)
  const developer = path.startsWith('/docs/developers') ? sdkNavigation.articles.find(item => item.slug === slug) : null
  if (developer) return {
    title: `${developer.title[locale]}｜Motexa Ride 主题开发文档`,
    description: locale === 'zh-CN' ? `Motexa Ride 主题 SDK：${developer.title[locale]}。查看公开接口、示例和开发说明。` : `Motexa Ride theme SDK: ${developer.title[locale]}. Public APIs, examples and development guidance.`,
    type: 'article',
  }
  return null
}

function setMeta(selector: string, attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.append(element)
  }
  element.content = content
}

export function applySeo(path: string, locale: SiteLocale) {
  const normalizedPath = path === '/' ? path : path.replace(/\/+$/, '')
  const page = pageFor(normalizedPath, locale)
  document.documentElement.lang = locale
  if (!page) {
    setMeta('meta[name="robots"]', 'name', 'robots', 'noindex,follow')
    return
  }
  document.head.querySelector('meta[name="robots"]')?.remove()
  const canonicalPath = normalizedPath === '/docs/developers' ? '/docs/developers/overview' : normalizedPath
  const url = new URL(canonicalPath, origin).href
  document.title = page.title
  setMeta('meta[name="description"]', 'name', 'description', page.description)
  setMeta('meta[property="og:title"]', 'property', 'og:title', page.title)
  setMeta('meta[property="og:description"]', 'property', 'og:description', page.description)
  setMeta('meta[property="og:type"]', 'property', 'og:type', page.type === 'article' ? 'article' : 'website')
  setMeta('meta[property="og:url"]', 'property', 'og:url', url)
  setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary')
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.rel = 'canonical'
    document.head.append(canonical)
  }
  canonical.href = url

  const schema = page.type === 'software'
    ? { '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'Motexa Ride', applicationCategory: 'UtilitiesApplication', operatingSystem: 'Android', url, sameAs: projectUrl, description: page.description }
    : page.type === 'article'
      ? { '@context': 'https://schema.org', '@type': 'TechArticle', headline: page.title, description: page.description, url, inLanguage: locale, isPartOf: { '@type': 'WebSite', name: 'Motexa Ride', url: origin } }
      : { '@context': 'https://schema.org', '@type': 'WebPage', name: page.title, description: page.description, url, inLanguage: locale, isPartOf: { '@type': 'WebSite', name: 'Motexa Ride', url: origin } }
  let structured = document.head.querySelector<HTMLScriptElement>('script[data-site-structured-data]')
  if (!structured) {
    structured = document.createElement('script')
    structured.type = 'application/ld+json'
    structured.dataset.siteStructuredData = ''
    document.head.append(structured)
  }
  structured.textContent = JSON.stringify(schema)
}
